import type { BlockBody, BookStayBody, CalendarBooking, LockedPeriod } from '../api/schemas';
import type { IsoDate } from '../calendar/monthGrid';
import { toE164 } from './phone';

export interface BookingDraft {
  /** An own stay, or a maintenance block that only needs dates. */
  kind: 'stay' | 'block';
  bookingFor: 'self' | 'someone_else';
  visitorName: string;
  /** National number as typed; the country is separate. */
  visitorPhone: string;
  visitorCountry: string;
  /** `''` until chosen. */
  from: IsoDate;
  to: IsoDate;
  comment: string;
  street: string;
  postcode: string;
  city: string;
  billingPhone: string;
  billingCountry: string;
}

export type Step = 'who' | 'when' | 'billing' | 'confirm';

/** When comes first: it holds the stay-or-block choice, and a block skips who and billing. */
export const steps = (needsBilling: boolean, kind: BookingDraft['kind'] = 'stay'): Step[] =>
  kind === 'block'
    ? ['when', 'confirm']
    : needsBilling
      ? ['when', 'who', 'billing', 'confirm']
      : ['when', 'who', 'confirm'];

const REQUIRED = 'This field is required';

/** Also the message for the server's `type_full` refusal. */
export const TYPE_FULL = 'All units of this type are booked on some of these nights';

const INVALID_PHONE = 'Enter a valid phone number';

const blank = (value: string): boolean => value.trim() === '';

const phoneError = (country: string, number: string): string | undefined =>
  blank(number) ? REQUIRED : toE164(country, number) === null ? INVALID_PHONE : undefined;

/**
 * Errors for a date range, keyed `from`/`to`: both chosen, from today, `to` after `from`, and no
 * night taken by `bookings` or `locked`. `[from, to)`: a stay may start on the day another one
 * ends, and end on the day one starts.
 */
export const validateDates = (
  d: { from: IsoDate; to: IsoDate },
  bookings: CalendarBooking[],
  today?: IsoDate,
  locked: LockedPeriod[] = []
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (d.from === '') {
    errors['from'] = 'Choose a date';
  } else if (today !== undefined && d.from < today) {
    errors['from'] = 'Choose a date from today';
  }
  if (d.to === '') {
    errors['to'] = 'Choose a date';
  } else if (d.from !== '' && d.to <= d.from) {
    errors['to'] = 'The end date must be after the start date';
  }
  if (errors['from'] === undefined && errors['to'] === undefined) {
    if (bookings.some((b) => d.from < b.to && b.from < d.to)) {
      errors['to'] = 'These dates are not free';
    } else if (locked.some((l) => d.from < l.to && l.from < d.to)) {
      errors['to'] = TYPE_FULL;
    }
  }
  return errors;
};

/**
 * Errors for one step, keyed by field name, values are English source strings (the caller
 * translates). `today` is optional so the form rules can be tested without a clock; the sheet
 * always passes it.
 */
export const validateStep = (
  step: Step,
  d: BookingDraft,
  bookings: CalendarBooking[],
  today?: IsoDate,
  locked: LockedPeriod[] = []
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (step === 'who' && d.bookingFor === 'someone_else') {
    if (blank(d.visitorName)) {
      errors['visitorName'] = REQUIRED;
    }
    const phone = phoneError(d.visitorCountry, d.visitorPhone);
    if (phone !== undefined) {
      errors['visitorPhone'] = phone;
    }
  }
  if (step === 'when') {
    Object.assign(errors, validateDates(d, bookings, today, locked));
  }
  if (step === 'billing') {
    if (blank(d.street)) {
      errors['street'] = REQUIRED;
    }
    if (blank(d.postcode)) {
      errors['postcode'] = REQUIRED;
    } else if (!/^\d{4}$/.test(d.postcode.trim())) {
      errors['postcode'] = 'Postcode must be 4 digits';
    }
    if (blank(d.city)) {
      errors['city'] = REQUIRED;
    }
    const phone = phoneError(d.billingCountry, d.billingPhone);
    if (phone !== undefined) {
      errors['billingPhone'] = phone;
    }
  }
  return errors;
};

export const toRequest = (
  d: BookingDraft,
  idempotencyKey: string,
  needsBilling: boolean
): BookStayBody => {
  const comment = d.comment.trim();
  return {
    from: d.from,
    to: d.to,
    bookingFor: d.bookingFor,
    ...(d.bookingFor === 'someone_else'
      ? {
          visitor: {
            name: d.visitorName.trim(),
            phone: toE164(d.visitorCountry, d.visitorPhone) ?? d.visitorPhone.trim()
          }
        }
      : {}),
    ...(comment === '' ? {} : { comment }),
    ...(needsBilling
      ? {
          billingCustomer: {
            street: d.street.trim(),
            postcode: d.postcode.trim(),
            city: d.city.trim(),
            phone: toE164(d.billingCountry, d.billingPhone) ?? d.billingPhone.trim()
          }
        }
      : {}),
    idempotencyKey
  };
};

export const toBlockRequest = (d: BookingDraft): BlockBody => {
  const comment = d.comment.trim();
  return { from: d.from, to: d.to, ...(comment === '' ? {} : { comment }) };
};
